import 'react-international-phone/style.css';

import {
  BaseTextFieldProps,
  Box,
  Divider,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { uniq } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CountryIso2,
  FlagImage,
  UsePhoneInputConfig,
  defaultCountries,
  usePhoneInput,
} from 'react-international-phone';

import {
  resolveCountryOptions,
  resolveInputPlaceholder,
} from '~/components/GenericForm/PhoneNumberField/data';
import { useSxStyles } from '~/components/GenericForm/PhoneNumberField/useStyles';
import { useCompanyData } from '~/data/company';
import { useAllLegalEntities } from '~/data/legalEntity';
import { mergeSxProps } from '~/utils/helpers/sxProps';

export interface PhoneNumberFieldProps extends BaseTextFieldProps {
  value: string;
  onChange: (phone: string) => void;
  phoneInputConfig?: UsePhoneInputConfig;
}

export const PhoneNumberField: React.FC<PhoneNumberFieldProps> = (props) => {
  const { value, onChange, phoneInputConfig, ...restProps } = props;
  const { disabled } = restProps;

  const { t } = useTranslation();

  const sxStyles = useSxStyles();

  const { companyData } = useCompanyData();
  const { legalEntities } = useAllLegalEntities();

  const {
    defaultCountry,
    preferredCountries,
    countryOptions,
    showOptionsDivider,
  } = useMemo(() => {
    const legalEntityCountryCodes = uniq(
      legalEntities.map((le) => le.fLegalEntityCountry.toLowerCase()),
    );
    const companyCountryCode = companyData.fComCountry.toLowerCase();

    const defaultCountry =
      companyCountryCode.length > 0
        ? companyCountryCode
        : legalEntityCountryCodes[0] ?? undefined;

    const countryOptions = resolveCountryOptions(
      defaultCountries,
      legalEntityCountryCodes,
    );

    const showOptionsDivider =
      countryOptions.preferred.length > 0 &&
      countryOptions.countries.length > 0;

    return {
      defaultCountry,
      preferredCountries: legalEntityCountryCodes,
      countryOptions,
      showOptionsDivider,
    };
  }, [companyData, legalEntities]);

  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
    usePhoneInput({
      value,
      countries: defaultCountries,
      preferredCountries,
      defaultCountry,
      onChange: (data) => {
        // Pass empty value if no inputValue, otherwise pass phone number which includes dial code
        onChange(data.inputValue.length > 0 ? data.phone : '');
      },
      disableDialCodeAndPrefix: true,
      ...(phoneInputConfig ?? {}),
    });

  const { placeholder } = useMemo(() => {
    return {
      placeholder: resolveInputPlaceholder(country),
    };
  }, [country]);

  return (
    <TextField
      variant="outlined"
      label={t('phone')}
      color={'primary'}
      placeholder={placeholder}
      value={inputValue}
      onChange={handlePhoneValueChange}
      type={'tel'}
      inputRef={inputRef}
      InputProps={{
        startAdornment: (
          <InputAdornment position={'start'} sx={sxStyles.inputAdornment}>
            <Select
              MenuProps={{
                sx: sxStyles.menuProps,
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
              }}
              sx={sxStyles.countrySelect}
              value={country.iso2}
              onChange={(e) => setCountry(e.target.value as CountryIso2)}
              renderValue={(value) => (
                <FlagImage
                  iso2={value}
                  style={{
                    ...sxStyles.selectedFlag,
                    ...(disabled ? sxStyles.selectedFlagDisabled : {}),
                  }}
                />
              )}
              disabled={disabled}
            >
              {countryOptions.preferred.map((country, index) => {
                return (
                  <MenuItem
                    key={`preferred-${index}`}
                    value={country.iso2}
                    sx={sxStyles.menuItem}
                  >
                    <FlagImage
                      iso2={country.iso2}
                      style={sxStyles.optionFlag}
                    />
                    <Typography sx={sxStyles.optionName}>
                      {country.name}
                    </Typography>
                    <Typography sx={sxStyles.optionDialCode}>
                      +{country.dialCode}
                    </Typography>
                  </MenuItem>
                );
              })}
              {showOptionsDivider && <Divider />}
              {countryOptions.countries.map((country, index) => {
                return (
                  <MenuItem
                    key={`country-${index}`}
                    value={country.iso2}
                    sx={sxStyles.menuItem}
                  >
                    <FlagImage
                      iso2={country.iso2}
                      style={sxStyles.optionFlag}
                    />
                    <Typography sx={sxStyles.optionName}>
                      {country.name}
                    </Typography>
                    <Typography sx={sxStyles.optionDialCode}>
                      +{country.dialCode}
                    </Typography>
                  </MenuItem>
                );
              })}
            </Select>
            <Box
              sx={mergeSxProps(
                sxStyles.dialCode,
                disabled ? sxStyles.dialCodeDisabled : undefined,
              )}
            >
              +{country.dialCode}
            </Box>
            <Divider orientation={'vertical'} />
          </InputAdornment>
        ),
      }}
      {...restProps}
    />
  );
};
